/**
 * Off-DOM image probing with an in-memory session cache.
 * Shared by team crest / player headshot components so remounts skip the
 * placeholder → real swap once a URL has been resolved.
 */

const resolvedByKey = new Map<string, string | null>();
const inflightByKey = new Map<string, Promise<string | null>>();

export function imageUrlListCacheKey(urls: readonly string[]): string {
  return (urls || []).filter(Boolean).join("\0");
}

/**
 * @returns `undefined` if not yet probed; `null` if all candidates failed; otherwise the working URL.
 */
export function getCachedResolvedImageUrl(
  urls: readonly string[],
): string | null | undefined {
  const list = (urls || []).filter(Boolean);
  if (list.length === 0) return null;
  const key = imageUrlListCacheKey(list);
  if (!resolvedByKey.has(key)) return undefined;
  return resolvedByKey.get(key) ?? null;
}

/**
 * First candidate URL that loads successfully (off-DOM), or null.
 * Same request count as an onError chain, but the visible <img> never needs to
 * show a broken icon when callers start from a placeholder or optimistic URL.
 */
export function resolveFirstLoadingImageUrl(
  urls: readonly string[],
): Promise<string | null> {
  const list = (urls || []).filter(Boolean);
  if (list.length === 0) return Promise.resolve(null);

  const key = imageUrlListCacheKey(list);
  if (resolvedByKey.has(key)) {
    return Promise.resolve(resolvedByKey.get(key) ?? null);
  }

  const existing = inflightByKey.get(key);
  if (existing) return existing;

  const promise = new Promise<string | null>((resolve) => {
    let i = 0;
    const finish = (result: string | null) => {
      resolvedByKey.set(key, result);
      inflightByKey.delete(key);
      resolve(result);
    };
    const tryNext = () => {
      if (i >= list.length) {
        finish(null);
        return;
      }
      const probe = new Image();
      const url = list[i]!;
      probe.onload = () => {
        if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
          finish(url);
        } else {
          i += 1;
          tryNext();
        }
      };
      probe.onerror = () => {
        i += 1;
        tryNext();
      };
      probe.src = url;
    };
    tryNext();
  });

  inflightByKey.set(key, promise);
  return promise;
}
